import {
  APP_ID,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { IMAGE_LOADER, NgOptimizedImage, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { BrowserModule, provideClientHydration, withEventReplay, withI18nSupport } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouteReuseStrategy, provideRouter, withInMemoryScrolling } from '@angular/router';
import { BuilderModule } from '@builder.io/angular';
import { BuilderConfigService, CONFIG_LOADER, ConfigModule } from '@scalefast/config-angular';
import { MaintenanceModule } from '@scalefast/ecommerce-components';
import {
  BreadcrumbService,
  BuilderService,
  GenericLogger,
  MECACHIS_DEPENDENCY_SERVICE,
  METAPRODUCT_SELECTOR_SERVICE,
  MeCachisDependencyServiceNoOp,
  MetaproductSelectorCheapestService,
  PRODUCT_SLUGGER_SERVICE,
  ProductBreadcrumbService,
  ProductService,
  ProductSluggerService,
  SearchResultsPageService,
  imageLoader,
  provideAllGenericAnalytics,
} from '@scalefast/ecommerce-core';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { ConfigLoaderService } from 'src/config/config-loader.service';
import { IS_BUILDER_EDITOR } from 'src/types/tokens/inject-tokens';
import { routes } from './app.routes';
import { CustomReuseStrategy } from './app.routes.strategy';
import { MISSING_PRODUCT_IMAGE_URL } from './app.tokens';
import { GlobalErrorHandler } from './shared/helpers/global-error-handler';
import { ProductWrapperService } from './shared/services';
import { CustomBreadcrumbService } from './shared/services/breadcrumb.service';
import { CustomProductBreadcrumbService } from './shared/services/product-breadcrumb.service';
import { CustomStoreSearchService } from './shared/services/store-search.service';
import { WindowRef } from './shared/services/window-ref.service';
import { providePrefetch } from './shared/provide-prefetch';
import { providePageResolver } from './shared/provide-page-resolvers';
import { BuilderGen2Service } from './shared/services/builder-gen2.service';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');

const locale = new ConfigLoaderService().getLocalizationConfig().v4Locale;
function getBuilderIOApiKey(): string {
  const configLoader = new ConfigLoaderService();
  const builderConfig = new BuilderConfigService(configLoader);
  return builderConfig.get().builderIOApiKey;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })),
    importProvidersFrom(
      BrowserModule,
      ConfigModule,
      BuilderModule.forRoot(getBuilderIOApiKey()),
      MaintenanceModule,
      GoogleTagManagerModule.forRoot({ id: 'GTM-N94XXFB' }), //Main GTM Container ID, shared by IT, FR and ES
      NgOptimizedImage,
    ),
    {
      provide: BuilderService,
      useClass: BuilderGen2Service,
    },
    provideClientHydration(withI18nSupport(), withEventReplay()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withFetch()),
    provideAnimationsAsync(),
    providePageResolver(),
    providePrefetch(),
    provideAllGenericAnalytics(),
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    {
      provide: MECACHIS_DEPENDENCY_SERVICE,
      useClass: MeCachisDependencyServiceNoOp,
    },
    {
      provide: CONFIG_LOADER,
      useClass: ConfigLoaderService,
    },
    {
      provide: PRODUCT_SLUGGER_SERVICE,
      useClass: ProductSluggerService,
    },
    {
      provide: METAPRODUCT_SELECTOR_SERVICE,
      useClass: MetaproductSelectorCheapestService,
    },
    {
      provide: 'WindowRef',
      useClass: WindowRef,
    },
    {
      provide: APP_ID,
      useValue: 'serverApp',
    },
    {
      provide: IMAGE_LOADER,
      useValue: imageLoader,
    },
    {
      provide: LOCALE_ID,
      useValue: locale,
    },
    {
      provide: ProductBreadcrumbService,
      useClass: CustomProductBreadcrumbService,
    },
    {
      provide: ProductService,
      useClass: ProductWrapperService,
    },
    {
      provide: BreadcrumbService,
      useClass: CustomBreadcrumbService,
    },
    {
      provide: MISSING_PRODUCT_IMAGE_URL,
      useValue: 'assets/images/product-default-img.png',
    },
    {
      provide: 'GenericLogger',
      useClass: GenericLogger,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: SearchResultsPageService,
      useClass: CustomStoreSearchService,
    },
    {
      provide: IS_BUILDER_EDITOR,
      useFactory: (): boolean => {
        if (typeof window !== 'undefined') {
          return window.location.search.indexOf('__builder_editing__') > -1;
        }
        return false;
      },
    },
  ],
};
